import React from "react";
import GameMap from "./Map";
import {
  PlayerInfo,
  LocationInfo,
  GameCondition,
  TeamInfoResponse,
  Event,
  GameMode,
  Coordinates,
  LocationInfo_Type,
} from "../api/copenhunt/v1/copenhunt_pb";
import { Shape, Color } from "../utils";
import UserInfoPanel from "./UserInfoPanel";

interface UserGameViewProps {
  visiblePlayers: PlayerInfo[];
  teamColors: { [teamId: string]: Color };
  teamNumbers: { [teamId: string]: number };
  objectiveShapes: { [objectiveId: string]: Shape };
  visibleLocations: LocationInfo[];
  playerId: string;
  objectiveId: string;
  teamId: string;
  teamInfoById: { [teamId: string]: TeamInfoResponse };
  locationDelaySeconds: number;
  playerName: string;
  playerCoordinates: Coordinates | null;
  overview: {
    condition: GameCondition | undefined;
    secondsToNextSwitch: number | undefined;
    isPaused: boolean | undefined;
    roundDurationSeconds: number | undefined;
    secondsToNextPing?: number;
    pingIntervalSeconds?: number;
    events: Event[];
    latestPingTimestamp: number;
    currentTimestamp: number;
    mode: GameMode;
  };
  onSendPing: () => void;
}

const UserGameView: React.FC<UserGameViewProps> = ({
  visiblePlayers,
  teamColors,
  teamNumbers,
  objectiveShapes,
  visibleLocations,
  playerId,
  objectiveId,
  teamId,
  teamInfoById,
  locationDelaySeconds,
  overview,
  playerName,
  onSendPing,
  playerCoordinates,
}) => {
  const isHunter = teamInfoById[teamId]?.isHunter ?? false;

  const renderContent = () => {
    let locationsForMap = visibleLocations;
    if (overview.mode === GameMode.DISTANCES_ONLY && !isHunter) {
      // In distance-only mode, the only treasure the player is allowed to see
      // (if any) is the one they're carrying.
      locationsForMap = visibleLocations.filter(
        (loc) => {
          if (loc.type !== LocationInfo_Type.TREASURE) {
            // This isn't a treasure, so it's visible.
            return true;
          }
          if (objectiveId && loc.id === objectiveId) {
            // This is the treasure the player is carrying.
            return true;
          }
          // This is a treasure, but it's not the one the player is carrying.
          return false;
        }
      );
    }

    // Default view: normal mode or hunter
    return (
      <GameMap
        playerInfos={visiblePlayers}
        teamColors={teamColors}
        teamNumbers={teamNumbers}
        objectiveShapes={objectiveShapes}
        locations={locationsForMap}
        teamInfoById={teamInfoById}
        locationDelaySeconds={locationDelaySeconds}
        forPlayerId={playerId}
        forTeamId={teamId}
        latestPingTimestamp={overview.latestPingTimestamp}
        pingIntervalSeconds={overview.pingIntervalSeconds || 0}
        mode={overview.mode}
      />
    );
  };

  return (
    <div className="game-container">
      {renderContent()}
      <UserInfoPanel
        teamColors={teamColors}
        teamId={teamId}
        teamInfoById={teamInfoById}
        overview={overview}
        visibleLocations={visibleLocations}
        objectiveId={objectiveId}
        objectiveShapes={objectiveShapes}
        isHunter={isHunter}
        teamNumbers={teamNumbers}
        playerName={playerName}
        onSendPing={onSendPing}
        playerCoordinates={playerCoordinates}
      />
    </div>
  );
};

export default UserGameView;
